import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { Link, Route } from 'react-router-dom';
import { useQuery, useMutation, useApolloClient } from 'react-apollo';
import concat from 'lodash/concat';
import capitalize from 'lodash/capitalize';
import useSnack from 'hooks/useSnack';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListSubheader from '@material-ui/core/ListSubheader';
import IconButton from '@material-ui/core/IconButton';
import ArrowBackIosOutlinedIcon from '@material-ui/icons/ArrowBackIosOutlined';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CopyIcon from '@material-ui/icons/FileCopyOutlined';
import MoreIcon from '@material-ui/icons/MoreVertOutlined';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import EditIcon from '@material-ui/icons/Edit';
import DragHandleIcon from '@material-ui/icons/DragHandle';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
  arrayMove,
} from 'react-sortable-hoc';
import Spin from 'components/common/Spin';
import Button from 'components/common/Button';
import ModuleForm from './components/ModuleForm';
import {
  GET_MODULES,
  SAVE_MODULE,
  UPDATE_MODULE,
  SORT_MODULES,
  SORT_EXERCISES,
  UPDATE_EXERCISE,
  ADD_EXERCISE
} from 'graphql/admin/curriculum';
import {
  usePopupState,
  bindTrigger,
  bindMenu,
} from 'material-ui-popup-state/hooks';
import orderBy from 'lodash/orderBy'
import MoveModal from './components/MoveModal';
import CopyModal from './components/CopyModal';
import { useSelector } from 'react-redux';
import ViewExercise from '../../hc/curriculum/ViewExercise';
import Avatar from '@material-ui/core/Avatar';
import ListAltIcon from '@material-ui/icons/ListAlt';

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: theme.spacing(3),
  },
  list: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
  },
  addAction: {
    padding: theme.spacing(1),
    textAlign: 'center',
  },
}));

const DragHandle = SortableHandle(() => (
  <ListItemAvatar style={{ cursor: 'move' }}>
    <DragHandleIcon />
  </ListItemAvatar>
));

const SortableItem = SortableElement(({ module, selected, onEdit }) => (
  <ListItem
    button
    divider
    selected={module.id === Number(selected)}
    component={Link}
    ContainerComponent="div"
    to={`/curriculum/${module.curriculum_id}/${module.id}`}
  >
    <DragHandle />
    <ListItemText
      primary={module.name}
      secondary={capitalize(module.approach)}
    />
    {onEdit && (
      <ListItemSecondaryAction>
        <IconButton onClick={() => onEdit(module)}>
          <EditIcon />
        </IconButton>
      </ListItemSecondaryAction>
    )}
  </ListItem>
));

const SortableExercise = SortableElement(({ exercise, onCopy, onMove, canEdit }) => {
  const popup = usePopupState({ popupId: 'option', variant: 'popover' });

  const handleCopy = () => {
    popup.close();
    onCopy(exercise);
  };

  const handleMove = () => {
    popup.close();
    onMove(exercise);
  };

  if (!canEdit) {
    return (
      <ListItem
        divider
        button
        key={exercise.id}
        component={Link}
        to={`/curriculum/${exercise.module_id}/exercises/${exercise.id}`}
      >
        <ListItemAvatar>
          <Avatar>
            <ListAltIcon />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={exercise.name}
          secondary={`Duration: ${exercise.duration} minutes`}
        />
        { exercise.tags && (
          <Chip size="small" label={exercise.tags} />
        )}
      </ListItem>
    )
  }

  return (
    <ListItem divider ContainerComponent="div">
      <DragHandle />
      <ListItemText
        primary={exercise.name}
        secondary={`Duration: ${exercise.duration} minutes`}
      />
      { exercise.tags && (
        <Box marginRight={2}>
          <Chip size="small" label={exercise.tags} />
        </Box>
      )}
      <ListItemSecondaryAction>
        <IconButton {...bindTrigger(popup)}>
          <MoreIcon />
        </IconButton>
        <Menu {...bindMenu(popup)}>
          <MenuItem
            component={Link}
            to={`/curriculum/${exercise.module_id}/exercises/${exercise.id}`}
          >
            Edit
          </MenuItem>
          <MenuItem onClick={handleCopy}>Copy</MenuItem>
          <MenuItem onClick={handleMove}>Move</MenuItem>
        </Menu>
        {/*<Link to={`/curriculum/${exercise.module_id}/exercises/${exercise.id}`}>*/}
        {/*  <IconButton>*/}
        {/*    <EditIcon />*/}
        {/*  </IconButton>*/}
        {/*</Link>*/}
      </ListItemSecondaryAction>
    </ListItem>
  );
});

const SortableModules = SortableContainer(
  ({ items, selected, onEdit, className, disabled }) => {
    return (
      <List disablePadding className={className} component="div">
        {items.map((i, index) => (
          <SortableItem
            disabled={disabled}
            module={i}
            key={i.id}
            index={index}
            selected={selected}
            onEdit={onEdit}
          />
        ))}
      </List>
    );
  },
);

const SortableExercises = SortableContainer(
  ({ items, className, onCopy, onMove, canEdit }) => {
    return (
      <List
        disablePadding
        component="div"
        className={className}
        subheader={<ListSubheader disableSticky>Exercises</ListSubheader>}
      >
        {items.map((i, index) => (
          <SortableExercise
            exercise={i}
            key={i.id}
            index={index}
            onCopy={onCopy}
            onMove={onMove}
            canEdit={canEdit}
          />
        ))}
      </List>
    );
  },
);

const CurriculumList = ({ match, history }) => {
  const classes = useStyles();
  const [editing, setEditing] = useState();
  const [selected, setSelected] = useState();
  const [createModule] = useMutation(SAVE_MODULE);
  const [updateModule] = useMutation(UPDATE_MODULE);
  const [sortModules] = useMutation(SORT_MODULES);
  const [sortExercises] = useMutation(SORT_EXERCISES);
  const [updateExercise] = useMutation(UPDATE_EXERCISE);
  const [addExercise] = useMutation(ADD_EXERCISE);
  const user = useSelector(s => s.user.user);
  const canAdd = user.type === 'admin';

  const queryVar = {
    curriculum_id: match.params.id,
  };
  const { data } = useQuery(GET_MODULES, {
    variables: queryVar,
    fetchPolicy: 'cache-and-network',
  });
  const client = useApolloClient();
  const snack = useSnack();
  const { moduleId } = match.params;

  const onSaveModule = async (name, approach) => {
    if (editing.id) {
      await updateModule({
        variables: {
          id: editing.id,
          input: {
            name,
            approach,
          },
        },
      });
    } else {
      await createModule({
        variables: {
          input: {
            name,
            approach,
            curriculum_id: match.params.id,
          },
        },
        update: (
          cache,
          {
            data: {
              modules: { returning },
            },
          },
        ) => {
          try {
            const { modules } = cache.readQuery({
              query: GET_MODULES,
              variables: queryVar,
            });
            cache.writeQuery({
              query: GET_MODULES,
              variables: queryVar,
              data: {
                modules: concat(modules, returning),
              },
            });
          } catch (e) {
            console.log(e);
          }
        },
      });
    }
    setEditing();
    snack.success('Module saved');
  };

  const onCancelEdit = () => setEditing();

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const old = data.modules[oldIndex];
    const updated = data.modules[newIndex];
    old.order = newIndex;
    updated.order = oldIndex;

    client.cache.writeQuery({
      query: GET_MODULES,
      variables: queryVar,
      data: {
        modules: arrayMove(data.modules, oldIndex, newIndex),
      },
    });
    sortModules({
      variables: {
        id1: old.id,
        order1: newIndex,
        id2: updated.id,
        order2: oldIndex,
      },
    });
  };

  const onExerciseSortEnd = ({ oldIndex, newIndex }) => {
    const old = exercises[oldIndex];
    const updated = exercises[newIndex];
    old.order = newIndex;
    updated.order = oldIndex;

    const module = data.modules.find((m) => m.id === Number(moduleId));
    module.exercises = arrayMove(module.exercises, oldIndex, newIndex);

    client.cache.writeQuery({
      query: GET_MODULES,
      variables: queryVar,
      data: {
        modules: data.modules,
      },
    });
    sortExercises({
      variables: {
        id1: old.id,
        order1: newIndex,
        id2: updated.id,
        order2: oldIndex,
      },
    });
  };

  const onDeleteModule = async () => {
    if (editing.id) {
      await updateModule({
        variables: {
          id: editing.id,
          input: {
            deleted: true,
          },
        },
        update: (proxy) => {
          if (+match.params.moduleId === editing.id) {
            history.push(`/curriculum/${match.params.id}`);
          }
          const { modules } = proxy.readQuery({
            query: GET_MODULES,
            variables: queryVar,
          });
          proxy.writeQuery({
            query: GET_MODULES,
            variables: queryVar,
            data: {
              modules: modules.filter((m) => m.id !== editing.id),
            },
          });
        },
      });
      onCancelEdit();
    }
  };

  const onMoveExercise = (target, exercise) => {
    setSelected();
    return updateExercise({
      variables: {
        id: exercise.id,
        input: {
          module_id: target,
        },
      },
      update: (cache) => {
        const { modules } = cache.readQuery({
          query: GET_MODULES,
          variables: queryVar,
        });
        const sourceModule = modules.find(m => m.id === exercise.module_id);
        const targetModule = modules.find(m => m.id === target);
        sourceModule.exercises = sourceModule.exercises.filter(e => e.id !== exercise.id);
        exercise.module_id = target;
        targetModule.exercises = orderBy([...targetModule.exercises, exercise], 'order');

        client.cache.writeQuery({
          query: GET_MODULES,
          variables: queryVar,
          data: {
            modules,
          },
        });
      }
    }).then(
      () => {
        snack.success('Success');
      },
      (e) => snack.error(e.message),
    );
  };

  const onCopyExercise = (target, exercise) => {
    const input = {
      ...exercise,
      module_id: target,

    }

    delete input.id;
    delete input.__typename;

    return addExercise({
      variables: {
        input
      }
    }).then(
      () => {
        setSelected();
        snack.success('Exercise copied')
      },
      e => snack.error(e.message)
    )
  }

  const exercises = data?.modules.find((m) => m.id === Number(moduleId))
    ?.exercises;

  return (
    <div>
      <Helmet title="Curriculum builder" />
      <Box display="flex" alignItems="center" mb={2}>
        <IconButton edge="start" onClick={() => history.push('/curriculum')}>
          <ArrowBackIosOutlinedIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" color="textSecondary">
          {data?.curriculum.name}
        </Typography>
      </Box>
      {!data ? (
        <Spin spinning linear />
      ) : (
        <Grid container spacing={3}>
          <Grid item md={5}>
            <SortableModules
              useDragHandle
              disabled={!canAdd}
              className={classes.list}
              items={data.modules}
              selected={moduleId}
              onEdit={canAdd && setEditing}
              onSortEnd={onSortEnd}
            />
            {editing ? (
              <Card>
                <CardContent>
                  <ModuleForm
                    module={editing}
                    onSubmit={onSaveModule}
                    onCancel={onCancelEdit}
                    onDelete={onDeleteModule}
                  />
                </CardContent>
              </Card>
            ) : canAdd && (
              <div className={classes.addAction}>
                <Button
                  onClick={() => setEditing({})}
                  color="primary"
                  startIcon={<AddCircleOutlineIcon />}
                >
                  Add Module
                </Button>
              </div>
            )}
          </Grid>
          <Grid item md={7}>
            {!moduleId ? (
              <Typography variant="body2">Please select a module</Typography>
            ) : (
              <React.Fragment>
                <SortableExercises
                  useDragHandle
                  canEdit={canAdd}
                  items={exercises}
                  className={classes.list}
                  onSortEnd={onExerciseSortEnd}
                  onCopy={(exercise) =>
                    setSelected({ exercise, action: 'copy' })
                  }
                  onMove={(exercise) =>
                    setSelected({ exercise, action: 'move' })
                  }
                />
                {canAdd && (
                  <div className={classes.addAction}>
                    <Link to={`/curriculum/${moduleId}/exercises/add`}>
                      <Button
                        color="primary"
                        startIcon={<AddCircleOutlineIcon />}
                      >
                        Add Exercise
                      </Button>
                    </Link>
                  </div>
                )}
              </React.Fragment>
            )}
          </Grid>
        </Grid>
      )}
      {selected && selected.action === 'move' && (
        <MoveModal
          modules={data.modules}
          exercise={selected.exercise}
          onSelect={onMoveExercise}
          onClose={() => setSelected()}
        />
      )}
      {selected && selected.action === 'copy' && (
        <CopyModal
          exercise={selected.exercise}
          onClose={() => setSelected()}
          onSubmit={onCopyExercise}
        />
      )}
    </div>
  );
};

export default CurriculumList;
